/**
 * Composition root. Nothing here knows how a bell is synthesised, how a PIN is
 * hashed, or how a band is drawn — it owns the wiring and the tick, and that is
 * all. Every module below is built against the interfaces declared in its own
 * file; this is the only place they meet.
 *
 * The loop, in one breath: correct the clock from the server, resolve today
 * into a list of bell events for THIS tablet's wing, and every 250ms fire the
 * events that just came due. Everything else is presentation.
 */

import { createAudioEngine } from './core/audio';
import { createClock } from './core/clock';
import { createStore } from './core/store';
import { requestWakeLock } from './core/wake';
import { chicagoDateISO } from './core/time';
import {
  dueEvents,
  currentBand,
  isLowerSchoolGroup,
  nextPeriod,
  nextSchoolDay,
  periodRemaining,
  resolveDay,
} from './core/schedule';
import { clearSession, loadSession } from './core/auth';
import { mountPinPad } from './ui/pinpad';
import { mountKiosk, type KioskView } from './ui/kiosk';
import { mountAdmin } from './ui/admin';
import type { BellEvent, DayPlan, Session } from './shared/types';
import './styles/app.css';

/** How often we re-evaluate. Fine enough to land a bell on the second. */
const TICK_MS = 250;

/**
 * If the tablet was asleep, throttled, or wedged, do not machine-gun every
 * bell we missed. Ring only what is genuinely still current.
 */
const MAX_LATE_MS = 30_000;

const app = document.getElementById('app')!;

const clock = createClock();
const audio = createAudioEngine();
const store = createStore(clock);

let session: Session | null = loadSession();
let kiosk: KioskView | null = null;
let unmount: (() => void) | null = null;
let plan: DayPlan | null = null;
let planDate = '';
let planVersion = -1;
let lastTick = 0;

// ---------------------------------------------------------------- the tick

/**
 * The group this tablet rings. The published teacher record wins over the
 * session stamp, so moving a room from Primary School onto K-2 (or 3-6) takes
 * effect on the next schedule fetch — nobody has to re-enter a PIN.
 */
function tabletWing(): string {
  const live = store.doc.teachers.find((t) => t.id === session!.teacherId);
  return live?.wing ?? session!.wing;
}

function planFor(nowMs: number): DayPlan {
  const date = chicagoDateISO(nowMs);
  const doc = store.doc;
  const wing = tabletWing();
  if (!plan || date !== planDate || doc.version !== planVersion || plan.wing !== wing) {
    plan = resolveDay(doc, date, wing);
    planDate = date;
    planVersion = doc.version;
    // A published change can move a bell we have already passed; re-arm from
    // now rather than replaying the morning.
    lastTick = nowMs;
  }
  return plan;
}

/** The next actual ring, skipping one-minute warnings. */
function nextRing(today: DayPlan, nowMs: number): BellEvent | null {
  return today.events.find((e) => e.kind === 'bell' && e.atMs > nowMs) ?? null;
}

function fire(event: BellEvent): void {
  audio.play(event.kind === 'warn' ? 'warn' : 'bell');
  console.info(
    `[bell] ${event.kind} @ ${new Date(event.atMs).toISOString()} — ${event.label}`,
  );
}

function tick(): void {
  if (!session) return;
  const now = clock.now();
  const today = planFor(now);

  if (lastTick && now > lastTick) {
    for (const event of dueEvents(today, lastTick, now)) {
      if (now - event.atMs <= MAX_LATE_MS) fire(event);
    }
  }
  lastTick = now;

  // The next-bell line is only a real ring, never the one-minute song. Primary's
  // large number is minutes left in the current period, even when it is silent.
  const nextBell = nextRing(today, now);
  const wing = tabletWing();
  const remaining = periodRemaining(today, now, wing);

  kiosk?.update({
    nowMs: now,
    session,
    plan: today,
    next: nextBell,
    nextPeriod: isLowerSchoolGroup(wing) ? nextPeriod(today, now) : null,
    periodLeftMs: remaining?.ms ?? null,
    periodLabel: remaining?.label ?? '',
    // The office can rename a bell group, so the wall reads the name out of the
    // published schedule rather than a constant. Falls back to the raw id so a
    // tablet whose group was deleted still says something truthful.
    groupName: store.doc.grids[wing]?.title ?? wing,
    current: currentBand(today, now),
    // Monday and Friday are home days, and so is any afternoon after the last
    // bell. Rather than an empty screen, point at the next morning that rings.
    nextSchoolPlan:
      today.isSchoolDay && nextBell
        ? null
        : nextSchoolDay(store.doc, today.date, wing),
    armed: audio.armed,
    online: store.online,
    clockTrusted: clock.trusted,
    note: today.note,
  });
}

// ------------------------------------------------------------------ routing

function render(): void {
  unmount?.();
  kiosk?.destroy();
  unmount = null;
  kiosk = null;
  app.replaceChildren();

  const wantsAdmin = location.pathname.startsWith('/admin');

  if (!session || (wantsAdmin && session.role !== 'admin')) {
    unmount = mountPinPad(app, {
      mode: wantsAdmin ? 'admin' : 'login',
      // Fires synchronously inside every tap. Android only honours an
      // AudioContext resume inside a real gesture turn, so this is where the
      // day's audio is unlocked — the teacher's PIN tap IS the unlock.
      onGesture: () => void audio.armFromGesture(),
      onSuccess: (s) => {
        session = s;
        plan = null;
        render();
      },
    });
    return;
  }

  if (wantsAdmin) {
    unmount = mountAdmin(app, {
      session,
      store,
      clock,
      onExit: () => {
        history.pushState({}, '', '/');
        render();
      },
    });
    return;
  }

  kiosk = mountKiosk(app, {
    // The arm overlay's tap. Same gesture rule as the PIN pad.
    onArmTap: () => void audio.armFromGesture(),
    onTestBell: () => audio.play('bell'),
    volume: audio.volume,
    onVolume: (v) => audio.setVolume(v),
    onOpenAdmin: () => {
      history.pushState({}, '', '/admin');
      render();
    },
    onSignOut: () => {
      clearSession();
      session = null;
      render();
    },
  });
  plan = null;
  tick();
}

// -------------------------------------------------------------------- boot

async function boot(): Promise<void> {
  // Try to arm without a gesture. Some kiosk builds allow it; most do not.
  // Whatever the answer, the kiosk shows an honest arm state.
  void audio.tryArm();

  store.onChange(() => {
    plan = null;
    tick();
  });
  store.start();

  void requestWakeLock();
  window.addEventListener('popstate', render);

  render();
  setInterval(tick, TICK_MS);

  if ('serviceWorker' in navigator && import.meta.env.PROD) {
    try {
      await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    } catch (err) {
      // A failed SW registration must not stop the bells; it only costs offline.
      console.error('service worker registration failed', err);
    }
  }
}

void boot();

// Exposed for the Playwright QA walks, which drive the real UI rather than
// poking internals — but need to pin the clock to "Thursday 10:14" to do it.
declare global {
  interface Window {
    __valorBells?: {
      clock: typeof clock;
      audio: typeof audio;
      store: typeof store;
      getPlan: () => DayPlan | null;
      tick: () => void;
    };
  }
}
window.__valorBells = { clock, audio, store, getPlan: () => plan, tick };
